class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();
      this.abortController = new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) {
      return;
    }

    const occurrences =
      currentButtonText.split(previousTerm).length - 1;

    if (occurrences > 1) {
      return;
    }

    searchForTextElement.innerText =
      currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';

    const selectedElement = this.querySelector(
      '[aria-selected="true"]'
    );

    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li[role="option"], button.predictive-search__item'
      )
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;

    if (moveUp && !selectedElement) return;

    let activeElementIndex = 0;

    const selectedElementIndex = selectedElement
      ? allVisibleElements.indexOf(selectedElement)
      : -1;

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex <= 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    const activeElement =
      allVisibleElements[activeElementIndex];

    if (!activeElement || activeElement === selectedElement) {
      return;
    }

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        'aria-selected',
        'false'
      );
    }

    if (activeElement.id) {
      this.input.setAttribute(
        'aria-activedescendant',
        activeElement.id
      );
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  buildSearchUrl(searchTerm) {
    const url = new URL(
      routes.predictive_search_url,
      window.location.origin
    );

    url.searchParams.set(
      'q',
      searchTerm
    );

    url.searchParams.set(
      'section_id',
      'predictive-search'
    );

    /*
     * IMPORTANT
     *
     * On force les produits dans la recherche.
     *
     * Cela évite de dépendre uniquement des réglages
     * Search & Discovery pour savoir quels types de
     * ressources doivent être recherchés.
     */
    url.searchParams.set(
      'resources[type]',
      'product,query,collection,page,article'
    );

    /*
     * On réserve des résultats pour chaque type.
     *
     * Une suggestion, collection ou page ne peut donc
     * pas prendre toute la limite et empêcher une pièce
     * automobile correspondante d'apparaître.
     */
    url.searchParams.set(
      'resources[limit]',
      '6'
    );

    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );

    /*
     * IMPORTANT POUR LES RÉFÉRENCES AUTOMOBILES
     *
     * Shopify recherche normalement dans :
     * - titre
     * - type produit
     * - titre variante
     * - vendeur
     *
     * On ajoute explicitement :
     * - SKU
     * - code-barres
     */
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
    );

    /*
     * Les produits indisponibles restent recherchables,
     * mais sont placés après les produits disponibles.
     */
    url.searchParams.set(
      'resources[options][unavailable_products]',
      'last'
    );

    return url.toString();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .trim()
      .toLowerCase();

    this.setLiveRegionLoadingState();

    /*
     * Résultat déjà en cache
     */
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      const searchDeferred =
        this.dispatchSearchUpdateEvent(searchTerm);

      searchDeferred?.resolve({
        totalCount: this.getTotalResultCount(),
      });

      return;
    }

    /*
     * On annule l'ancienne requête si l'utilisateur
     * tape rapidement une autre référence.
     */
    this.abortController.abort();

    this.abortController =
      new AbortController();

    const searchDeferred =
      this.dispatchSearchUpdateEvent(searchTerm);

    const requestUrl =
      this.buildSearchUrl(searchTerm);

    fetch(requestUrl, {
      signal: this.abortController.signal,

      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Predictive search failed: ${response.status}`
          );

          error.status = response.status;

          throw error;
        }

        return response.text();
      })

      .then((text) => {
        const documentFromResponse =
          new DOMParser().parseFromString(
            text,
            'text/html'
          );

        const section =
          documentFromResponse.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!section) {
          throw new Error(
            'Predictive search section not found in Shopify response.'
          );
        }

        const resultsMarkup =
          section.innerHTML;

        /*
         * Cache partagé entre toutes les instances
         * de recherche du thème.
         */
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        this.renderSearchResults(
          resultsMarkup
        );

        searchDeferred?.resolve({
          totalCount:
            this.getTotalResultCount(),
        });
      })

      .catch((error) => {
        /*
         * Une requête annulée parce que l'utilisateur
         * continue à taper n'est pas une vraie erreur.
         */
        if (
          error?.name === 'AbortError' ||
          error?.code === 20
        ) {
          searchDeferred?.reject(error);
          return;
        }

        searchDeferred?.reject(error);

        this.close();

        console.error(error);
      });
  }

  getTotalResultCount() {
    return (
      parseInt(
        this.predictiveSearchResults
          .querySelector('[data-total-results]')
          ?.dataset.totalResults,
        10
      ) || 0
    );
  }

  dispatchSearchUpdateEvent(query) {
    const {
      SearchUpdateEvent,
    } = window.StandardEvents || {};

    if (!SearchUpdateEvent) {
      return null;
    }

    const deferred =
      SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
        },

        promise:
          deferred.promise,
      })
    );

    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText ||
          'Chargement'
      );
    }

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    this.setLiveRegionResults();

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (countElement) {
      this.setLiveRegionText(
        countElement.textContent.trim()
      );
    }
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector('.section-header')
        ?.getBoundingClientRect()
        .bottom || 0;

    this.resultsMaxHeight =
      Math.max(
        window.innerHeight -
          headerBottom,
        200
      );

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${
        this.resultsMaxHeight ||
        this.getResultsMaxHeight()
      }px`;

    this.setAttribute(
      'open',
      'true'
    );

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(
      clearSearchTerm
    );

    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';

      this.removeAttribute(
        'results'
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        'false'
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute(
      'loading'
    );

    this.removeAttribute(
      'open'
    );

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);